import { useEffect, useState } from 'react';
import { suppliersAPI } from '../../../lib/api';
import type { SupplierFormData } from './types';

/**
 * CR-6695: Determines whether the supplier being onboarded is an INTERCOMPANY
 * supplier — i.e. the supplier group chosen in Step 1 belongs to the same
 * organisation (root company-group tree) as one of the linked customer
 * companies from Step 2. The platform decides; the user cannot override it.
 *
 * Drives the Step 3 "Default Login Type" restriction:
 * - Standard supplier → login type locked to 'supplier' (read-only dropdown).
 * - Intercompany supplier → 'customer' becomes selectable.
 *
 * While eligibility is being (re)checked the dropdown stays locked, but saved
 * values are NOT coerced until the platform definitively answers "not
 * intercompany" — this avoids wiping a legitimately saved 'customer' choice
 * during the initial load of an intercompany draft.
 *
 * @param formData - Current wizard form state (group + customer selections)
 * @param setFormData - Wizard form state setter, used to snap stale 'customer'
 *   login types back to 'supplier' when eligibility is lost
 * @returns true only when the platform confirmed the supplier is intercompany
 */
export function useIntercompanyEligibility(
  formData: SupplierFormData,
  setFormData: React.Dispatch<React.SetStateAction<SupplierFormData>>,
): boolean {
  // null = unknown (check pending); boolean = platform's answer
  const [isIntercompany, setIsIntercompany] = useState<boolean | null>(null);

  const groupId = formData.is_part_of_group === true ? formData.supplier_group_id : undefined;
  // Join to a stable primitive so the effect re-runs only on real changes
  const customerIdsKey = (formData.customer_ids || []).join(',');

  useEffect(() => {
    if (!groupId || !customerIdsKey) {
      // Standalone supplier or no linked customers — never intercompany
      setIsIntercompany(false);
      return;
    }
    let cancelled = false;
    setIsIntercompany(null);
    suppliersAPI
      .checkIntercompanyEligibility({
        supplier_group_id: groupId,
        customer_ids: customerIdsKey.split(',').map(Number),
      })
      .then((res) => {
        if (!cancelled) setIsIntercompany(!!res.data.is_intercompany);
      })
      .catch((err: unknown) => {
        console.error('Failed to check intercompany eligibility:', err);
        // Fail closed: restriction applies when the platform cannot confirm
        if (!cancelled) setIsIntercompany(false);
      });
    return () => {
      cancelled = true;
    };
  }, [groupId, customerIdsKey]);

  // When the platform says "not intercompany", snap any stale 'customer'
  // login types back to 'supplier' so a choice made while eligible cannot
  // survive a group/customer change that removed eligibility.
  useEffect(() => {
    if (isIntercompany !== false) return;
    setFormData((prev) => {
      const primaryStale = prev.contact_login_type === 'customer';
      const additionalStale = prev.additional_contacts.some((c) => c.login_type === 'customer');
      if (!primaryStale && !additionalStale) return prev;
      return {
        ...prev,
        contact_login_type: primaryStale ? 'supplier' : prev.contact_login_type,
        additional_contacts: additionalStale
          ? prev.additional_contacts.map((c) =>
              c.login_type === 'customer' ? { ...c, login_type: 'supplier' } : c
            )
          : prev.additional_contacts,
      };
    });
  }, [isIntercompany, setFormData]);

  return isIntercompany === true;
}
